import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  FaAtom,
  FaBolt,
  FaBullseye,
  FaChartLine,
  FaCoins,
  FaDna,
  FaFlask,
  FaLayerGroup,
  FaMicroscope,
  FaProjectDiagram,
  FaRobot,
  FaShieldAlt,
} from "react-icons/fa";
import Navbar from "../../components/Navbar";
import BottomContent from "../../components/BottomContent";
import ScrollButton from "../../components/ScrollButton";
import ContactButton from "../../components/ContactButton";
import Thank from "../../components/Thank";
import DarkModeButton from "../../components/DarkModeButton";
import BioMolecularDesigner from "../../assets/img/Molecule/BioMolecularDesigner.png";
import ChemYuktiGPT from "../../assets/img/Molecule/ChemYuktiGPT.png";
import MolecularLogo from "../../assets/logo/MolecularScix.jpeg";
import ResearchMol from "../../assets/img/ResearchMol.jpg";
import Reaction from "../../assets/img/Molecule/Reactions.png";
import RetrosynthesisPlanner from "../../assets/img/Molecule/RetrosynthesisPlanner.png";
import SimulationEngine from "../../assets/img/Molecule/SimulationEngine.png";
import SpectroscopyAIInsight from "../../assets/img/Molecule/SpectroscopyAIInsight.png";
import videoSrc from "../../assets/img/Molecule/MolecularHero.mp4";
import molecularOverviewVideo from "../../assets/img/Molecule/MolecularSciX.ai-video.mp4";
import "../CustomStyle.css";
import "./MolecularScience.css";

const molecularFeatures = [
  {
    title: "Reaction Predictor",
    icon: <FaFlask size={22} />,
    image: Reaction,
    tone: "blue",
    text: "Predict chemical reaction outcomes with AI-powered intelligence. MolecularSciX.ai helps researchers identify possible final products, by-products, reaction feasibility, and suitable synthesis pathways, making reaction planning faster, smarter, and more reliable.",
  },
  {
    title: "Spectroscopy AI Insight",
    icon: <FaAtom size={22} />,
    image: SpectroscopyAIInsight,
    tone: "violet",
    text: "Automate spectral analysis and structure validation using AI. The platform supports interpretation and prediction of key analytical techniques including ¹H NMR, ¹³C NMR, IR, HRMS, UV-Vis, and Raman, helping researchers confirm chemical identity with reduced manual effort.",
  },
  {
    title: "Retrosynthesis Planner",
    icon: <FaProjectDiagram size={22} />,
    image: RetrosynthesisPlanner,
    tone: "green",
    text: "Plan efficient synthetic routes from target molecules to available starting materials. MolecularSciX.ai enables researchers to explore multiple pathways, define route depth, compare feasibility, and optimize synthesis strategies for faster experimental planning.",
  },
  {
    title: "Simulation Engine",
    icon: <FaMicroscope size={22} />,
    image: SimulationEngine,
    tone: "teal",
    text: "Analyses molecular behaviour, stability, interactions, and energetics with AI-assisted simulation capabilities. The platform supports molecular modeling and simulation workflows to help researchers understand molecular performance before costly lab experimentation.",
  },
  {
    title: "ChemYukti GPT",
    icon: <FaRobot size={22} />,
    image: ChemYuktiGPT,
    tone: "orange",
    text: "A chemistry-focused AI copilot designed to support researchers with scientific explanations, report generation, documentation, workflow guidance, and research query assistance. ChemYukti helps teams save time and simplify complex chemistry communication.",
  },
  {
    title: "Bio-Molecular Designer",
    icon: <FaDna size={22} />,
    image: BioMolecularDesigner,
    tone: "pink",
    text: "Design, visualize, and optimize molecules for advanced discovery workflows. MolecularSciX.ai supports bio-molecular modeling, protein-ligand interaction analysis, 3D visualization, and molecule design to accelerate the discovery of new therapeutic opportunities.",
  },
];

const benefits = [
  {
    icon: <FaBolt />,
    title: "Accelerated Discovery",
    text: "Reduce research timelines with faster molecular prediction, simulation, retrosynthesis, and analysis cycles.",
  },
  {
    icon: <FaBullseye />,
    title: "Enhanced Precision",
    text: "Improve accuracy in molecular design, compound prediction, reaction planning, and structural validation through AI-driven models.",
  },
  {
    icon: <FaCoins />,
    title: "Cost Efficiency",
    text: "Optimize laboratory and computational resources by reducing unnecessary trial-and-error experiments and lowering dependency on fragmented tools.",
  },
  {
    icon: <FaChartLine />,
    title: "Data Driven Insights",
    text: "Convert chemical, biological, spectral, and experimental data into actionable insights for smarter R&D decisions.",
  },
  {
    icon: <FaShieldAlt />,
    title: "Reduced Research Risk",
    text: "Evaluate feasibility, safety, toxicity, ADMET-related concerns, and synthesis complexity earlier in the discovery process.",
  },
  {
    icon: <FaLayerGroup />,
    title: "Unified Workflow",
    text: "Bring reaction prediction, retrosynthesis, spectroscopy, simulation, molecular design, and documentation into one connected AI platform.",
  },
];

const audiences = [
  "Pharma R&D teams",
  "Biotech companies",
  "Medicinal chemistry teams",
  "Synthetic chemistry labs",
  "Computational chemistry groups",
  "Academic research institutions",
  "Chemical and material science organizations",
  "AI-driven scientific innovation teams",
];

const whyCards = [
  {
    title: "From Slow Research to Intelligent Discovery",
    text: "Move beyond manual, fragmented, and time-consuming workflows with AI-powered molecular intelligence.",
  },
  {
    title: "From Data Complexity to Clear Insights",
    text: "Transform complex molecular datasets into meaningful predictions, summaries, reports, and research recommendations.",
  },
  {
    title: "From Experimental Risk to Confident Decisions",
    text: "Use AI to evaluate possible outcomes, feasibility, safety, and optimization opportunities before committing major lab resources.",
  },
  {
    title: "From Multiple Tools to One Unified Platform",
    text: "Reduce workflow fragmentation by bringing key molecular R&D capabilities into one integrated software environment.",
  },
];

const discoveryFlow = [
  "Molecular idea generation",
  "Prediction and feasibility",
  "Route planning",
  "Spectral validation",
  "Simulation insight",
  "Documentation",
];

export default function MolecularScience() {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.75;
    }
  }, []);

  return (
    <>
      <Navbar />

      <main className="molecular-page">
        <section className="molecular-hero">
          <video
            ref={videoRef}
            preload="auto"
            autoPlay
            loop
            muted
            playsInline
            className="molecular-hero-video"
          >
            <source src={videoSrc} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="molecular-hero-overlay"></div>

          <div className="container molecular-hero-inner">
            <div className="molecular-hero-copy">
              <div className="molecular-brand-chip">
                <img src={MolecularLogo} alt="MolecularSciX.ai" />
                <span>MolecularSciX.ai</span>
              </div>
              <h1>AI-Powered Molecular Science Platform</h1>
              <h2>Revolutionizing Molecular Discovery with Artificial Intelligence</h2>
              <p>
                MolecularSciX.ai is an advanced AI-powered molecular science
                platform designed to accelerate chemical research, drug
                discovery, synthesis planning, molecular modeling, and
                scientific decision-making. It helps research teams reduce
                discovery time, improve prediction accuracy, minimize
                experimental risk, and transform complex molecular data into
                actionable insights.
              </p>
              <div className="molecular-hero-actions">
                <Link to="/scheduleDemo" className="molecular-btn molecular-btn-primary">
                  Schedule Demo
                </Link>
                <a href="#molecular-overview" className="molecular-btn molecular-btn-secondary">
                  Watch Overview
                </a>
              </div>
            </div>

            <div className="molecular-lab-panel" aria-label="Molecular research workflow">
              <div className="molecular-panel-top">
                <span></span>
                <strong>AI Discovery Workspace</strong>
              </div>
              <div className="molecular-molecule-node">
                <FaAtom />
                <span>Target molecule</span>
              </div>
              <div className="molecular-flow-list">
                {discoveryFlow.map((item, index) => (
                  <div className="molecular-flow-item" key={item}>
                    <small>{String(index + 1).padStart(2, "0")}</small>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="molecular-section molecular-intro-section">
          <div className="container">
            <div className="row align-items-center gy-5">
              <div className="col-lg-6 molecular-intro-copy">
                <span className="molecular-eyebrow">Research intelligence</span>
                <h2>Transforming Molecular Research with AI</h2>
                <p>
                  Modern molecular R&D is complex, time-consuming, and
                  resource-intensive. Researchers often work across disconnected
                  tools, manual analysis, high-cost simulations, and
                  trial-anderror experimentation. MolecularSciX.ai solves this
                  challenge by providing a unified AI-driven platform that
                  supports scientists from molecular idea generation to synthesis
                  planning, validation, optimization, and documentation. The
                  platform enables faster research cycles, smarter molecular
                  decisions, and more efficient collaboration across chemistry,
                  biology, computational science, and pharma discovery teams.
                </p>
                <div className="molecular-metrics">
                  <div>
                    <strong>80%</strong>
                    <span>Faster Discovery</span>
                  </div>
                  <div>
                    <strong>AI Powered</strong>
                    <span>Molecular Decisions</span>
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="molecular-image-frame">
                  <img src={ResearchMol} alt="Molecular research visualization" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="molecular-section molecular-video-section" id="molecular-overview">
          <div className="container">
            <div className="molecular-section-heading">
              <span className="molecular-eyebrow">Platform overview</span>
              <h2>MolecularSciX.ai Platform Overview</h2>
              <p>
                Explore how MolecularSciX.ai brings AI-powered molecular
                prediction, analysis, and scientific automation into a unified
                research workflow.
              </p>
            </div>

            <div className="molecular-video-shell">
              <video
                controls
                preload="metadata"
                playsInline
                className="molecular-overview-video"
              >
                <source src={molecularOverviewVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </section>

        <section className="molecular-section molecular-solutions-section">
          <div className="container">
            <div className="molecular-section-heading">
              <span className="molecular-eyebrow">AI-driven molecular science solutions</span>
              <h2>Intelligent tools designed for real-world molecular research workflows.</h2>
              <p>
                MolecularSciX.ai empowers scientists and R&D teams with intelligent
                tools designed for real-world molecular research workflows.
              </p>
            </div>

            <div className="row g-4 molecular-feature-grid">
              {molecularFeatures.map((feature) => (
                <div className="col-lg-4 col-md-6" key={feature.title}>
                  <article className={`molecular-feature-card molecular-tone-${feature.tone}`}>
                    <img src={feature.image} alt={feature.title} />
                    <div className="molecular-feature-body">
                      <div className="molecular-feature-title">
                        <div className="molecular-feature-icon">{feature.icon}</div>
                        <h3>{feature.title}</h3>
                      </div>
                      <p>{feature.text}</p>
                    </div>
                  </article>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="molecular-section molecular-benefits-section">
          <div className="container">
            <div className="molecular-section-heading">
              <span className="molecular-eyebrow">Benefits</span>
              <h2>Benefits of MolecularSciX.ai</h2>
            </div>

            <div className="molecular-benefit-grid">
              <div className="molecular-benefit-hub" aria-hidden="true">
                <div className="molecular-benefit-hub-core">
                  <img src={MolecularLogo} alt="" />
                </div>
              </div>
              {benefits.map((benefit, index) => (
                <article className="molecular-benefit-item" key={benefit.title}>
                  <div className="molecular-benefit-icon">{benefit.icon}</div>
                  <small>{String(index + 1).padStart(2, "0")}</small>
                  <h3>{benefit.title}</h3>
                  <p>{benefit.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="molecular-section molecular-audience-section">
          <div className="container">
            <div className="row gy-4 align-items-start">
              <div className="col-lg-5">
                <span className="molecular-eyebrow">Built for modern pharma and chemical R&D</span>
                <h2>For teams working at the intersection of chemistry, biology, data, and AI.</h2>
                <p>
                  MolecularSciX.ai is designed for organizations working at the
                  intersection of chemistry, biology, data, and artificial
                  intelligence. Whether your team is discovering new drug candidates,
                  designing synthesis routes, validating molecular structures, or
                  optimizing research workflows, MolecularSciX.ai helps you move
                  faster with confidence.
                </p>
              </div>

              <div className="col-lg-7">
                <div className="molecular-audience-grid">
                  {audiences.map((item) => (
                    <div className="molecular-audience-pill" key={item}>
                      <FaFlask />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="molecular-section">
          <div className="container">
            <div className="molecular-section-heading">
              <span className="molecular-eyebrow">Why MolecularSciX.ai?</span>
              <h2>Move from fragmented research to intelligent discovery.</h2>
            </div>

            <div className="row g-4">
              {whyCards.map((card) => (
                <div className="col-md-6" key={card.title}>
                  <article className="molecular-why-card">
                    <h3>{card.title}</h3>
                    <p>{card.text}</p>
                  </article>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="molecular-section molecular-future-section">
          <div className="container">
            <div className="molecular-future-panel">
              <span className="molecular-eyebrow">Future vision</span>
              <h2>The Future of AI in Molecular Science</h2>
              <p>
                The future of molecular science lies in the fusion of artificial
                intelligence, chemistry, biology, and data-driven research. From
                drug discovery and personalized medicine to biomolecular engineering
                and sustainable material design, AI is redefining how scientific
                innovation happens. MolecularSciX.ai is built to support this future
                — making molecular discovery faster, smarter, more reliable, and
                more accessible for research-driven organizations.
              </p>
              <Link to="/scheduleDemo" className="molecular-btn molecular-btn-primary">
                Schedule Demo
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Thank />
      <BottomContent />
      <DarkModeButton />
      <ContactButton />
      <ScrollButton />
    </>
  );
}
